<script>
import { authComputed } from '@state/helpers';
import NotificationProgramSummary from '@components/notifications/notification-program-summary.vue';
import CardProgramSummary from '@components/cards/card-program-summary.vue';

export default {
  components: {
    NotificationProgramSummary,
    CardProgramSummary,
  },
  props: {
    client: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  computed: {
    ...authComputed,
    isImperial() {
      // Dynamically check trainer's preferred unit
      // Here not every trainer have the 'preferredUnit' flag so the default in imperial unless set metric
      return (
        this.currentUser?.roles?.isTrainer &&
        this.currentUser?.trainer?.preferredUnit !== 'metric'
      );
    },
    currentWeightInLb() {
      // Convert weight to lb
      if (
        this.client?.progress?.initial?.weight &&
        this.client.progress.initial.weight?.value > 0
      ) {
        const val = this.client.progress.initial.weight.value;
        return this.client.progress.initial.weight?.isImperial
          ? val
          : Math.floor(parseInt(val) * 2.20462);
      } else {
        return 0;
      }
    },
    currentWeightInKg() {
      // Convert weight to kg
      if (
        this.client?.progress?.initial?.weight &&
        this.client.progress.initial.weight?.value > 0
      ) {
        const val = this.client.progress.initial.weight.value;
        return this.client.progress.initial.weight?.isImperial
          ? Math.floor(parseInt(val) / 2.20462)
          : val;
      } else {
        return 0;
      }
    },
    currentWeight() {
      // Weight in trainer's preferred unit
      return this.isImperial ? this.currentWeightInLb : this.currentWeightInKg;
    },
    currentWaist() {
      // Waist in trainer's preferred unit
      const val = this.client?.progress?.initial?.waist?.value;
      if (this.isImperial) {
        return val > 0 ? val : 0;
      } else {
        return val > 0 ? Math.round(parseInt(val) * 2.54) : 0;
      }
    },
    currentHeightInInch() {
      // Convert weight to lb
      if (
        this.client?.progress?.initial?.height &&
        this.client.progress.initial.height?.value > 0
      ) {
        const val = this.client.progress.initial.height.value;
        return this.client.progress.initial.height?.isImperial
          ? val
          : Math.floor(parseInt(val) / 2.54);
      } else {
        return 0;
      }
    },
    calculateBmi() {
      const value =
        this.currentWeightInLb && this.currentHeightInInch
          ? (this.currentWeightInLb /
              this.currentHeightInInch /
              this.currentHeightInInch) *
            703
          : 0;
      return isNaN(value) ? value.toFixed(0) : value.toFixed(2);
    },
    injuries() {
      return this.client?.profile?.injuries &&
        this.client.profile.injuries.length > 0
        ? this.client.profile.injuries.join(', ')
        : '';
    },
  },
};
</script>

<template>
  <div class="personal-data-wrapper h-100">
    <CardProgramSummary :title="'Personal Data'">
      <template v-slot:icon>
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.023 5.871a.72.72 0 00-1.438.048v4.644c0 .007.002.015.005.022l2.041 4.52c.017.056.034.107.052.153.42 1.093.652 3.82.652 3.82l4.46-.549.64-3.677-.001-.037 1.983-4.28a.054.054 0 00.005-.022V6.002a.802.802 0 00-1.601-.054l-.267 3.991a.05.05 0 01-.005.02l-.935 1.89a1.351 1.351 0 00-.115.208l-.031.09-.17.344c-.79 1.267-2.912 1.459-3.944-.398L12.3 9.958a.05.05 0 01-.006-.019l-.271-4.068zm4.988 4.954c0 .897-.688 1.625-1.537 1.625-.85 0-1.538-.728-1.538-1.625 0-.898.688-1.625 1.538-1.625.849 0 1.537.727 1.537 1.625z" fill="#fff"/><path clip-rule="evenodd" d="M4.946 4a1 1 0 011-1h1.785a1 1 0 011 1v2H21.98V4a1 1 0 011-1h1.786a1 1 0 011 1v2h.196a.75.75 0 110 1.5h-.196V10a1 1 0 01-1 1H22.98a1 1 0 01-1-1V7.5H8.731V10a1 1 0 01-1 1H5.946a1 1 0 01-1-1V7.5H4.75a.75.75 0 010-1.5h.196V4zm10.886 16.534l3.597.98-.262 4.66a.915.915 0 001.819.192l.984-6.332a.084.084 0 00-.048-.089l-4.126-1.909-3.866.846-.494-.29S7.832 25.216 7.33 25.79c-.503.575.464 1.854 1.41 1.057.812-.683 5.712-5.077 7.092-6.314z"/></svg>
      </template>
      <div class="subdata">
        <span class="subtitle">WEIGHT</span>
        <span class="figure">{{
          currentWeight + (isImperial ? ' lb' : ' kg')
        }}</span>
      </div>
      <div class="divider-horizontal">&nbsp;</div>
      <div class="subdata">
        <span class="subtitle">WAIST</span>
        <span class="figure">{{
          currentWaist + (isImperial ? ' inch' : ' cm')
        }}</span>
      </div>
      <div class="divider-horizontal">&nbsp;</div>
      <div class="subdata">
        <span class="subtitle">BMI</span>
        <span class="figure">{{ calculateBmi }}</span>
      </div>
      <div class="divider-horizontal">&nbsp;</div>
      <template v-if="injuries">
        <NotificationProgramSummary heading="INJURIES">
          <div class="content mb-0">{{ injuries }}</div>
        </NotificationProgramSummary>
      </template>
      <NotificationProgramSummary
        v-if="calculateBmi === '0.00'"
        type="blue"
        :icon="true"
      >
        <template v-slot:icon>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path data-name="Subtraction 2" d="M12 24A12 12 0 013.515 3.515a12 12 0 1116.97 16.97A11.922 11.922 0 0112 24zm-.886-8.946V17h1.96v-1.946zM12.15 8.5a1.509 1.509 0 01.952.266.882.882 0 01.336.728 1.6 1.6 0 01-.182.742 3.785 3.785 0 01-.616.84 4.972 4.972 0 00-.84 1.224 3.1 3.1 0 00-.238 1.219v.56h1.064l.056-.392a2.675 2.675 0 01.322-.9 4.662 4.662 0 01.91-1.05 4.418 4.418 0 001.008-1.218 2.563 2.563 0 00.294-1.2 2.016 2.016 0 00-.378-1.219 2.424 2.424 0 00-1.038-.816 3.781 3.781 0 00-1.541-.293 5.181 5.181 0 00-1.778.321 4.483 4.483 0 00-1.456.854l.591 1.344A3.807 3.807 0 0112.15 8.5z"/></svg>
        </template>
        <h5>Personal Data</h5>
        <div class="content">Update Clients profile for Personal Data</div>
        <router-link
          :to="{
            name: 'clientAbout',
            params: { id: client._id, client: client },
          }"
          >Update Profile</router-link
        >
      </NotificationProgramSummary>
    </CardProgramSummary>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
.subdata {
  align-items: center;
  display: flex;
  justify-content: space-between;
  justify-self: center;
  margin-bottom: 0.8rem;
  margin-top: 0.8rem;
  width: 100%;
  .subtitle {
    color: $color-medium-gray-two;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.702381px;
    line-height: 19px;
    text-transform: uppercase;
  }
  .figure {
    color: $color-black-one;
    font-weight: normal;
    letter-spacing: -0.0666664px;
    mix-blend-mode: normal;
    opacity: 0.6;
    text-align: right;
  }
}
.col {
  padding-left: 0;
  padding-right: 0;
}
.divider-horizontal {
  align-self: center;
  border-top: 1px solid $color-gray;
  height: 0.5rem;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.5;
  opacity: 0.2;
  width: 100%;
}
</style>
